/**
 * Main layout component
 */

import React from 'react'
import { Outlet, useNavigate, useLocation } from 'react-router-dom'
import { Layout, Avatar, Dropdown, Typography, Space, Breadcrumb, Button } from 'antd'
import { isEmbedded } from '@/utils/embed'
import {
  ProjectOutlined,
  LogoutOutlined,
  UserOutlined,
  HomeOutlined,
  DatabaseOutlined,
  HistoryOutlined,
  SettingOutlined,
  DollarOutlined,
  ApiOutlined,
  BarChartOutlined,
} from '@ant-design/icons'
import type { MenuProps } from 'antd'
import { useAuthStore } from '@/stores/authStore'
import api from '@/services/api'
import AnalyzerStatus from '@/components/AnalyzerStatus'

const { Header, Content, Footer } = Layout
const { Text } = Typography

// * Role translation map
const getRoleLabel = (role: string): string => {
  const roleMap: Record<string, string> = {
    operator: 'Оператор',
    manager: 'Менеджер',
    admin: 'Администратор',
  }
  return roleMap[role] || role
}

function MainLayout() {
  const navigate = useNavigate()
  const location = useLocation()
  // * Embedded in an external iframe (e.g. VDL) — render a chrome-less Swiper
  const embedded = isEmbedded()
  const { operator, clearAuth } = useAuthStore()

  const handleLogout = async () => {
    try {
      await api.logout()
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      clearAuth()
      navigate('/login')
    }
  }

  const userMenuItems: MenuProps['items'] = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: operator?.full_name || operator?.username,
      disabled: true,
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Выйти',
      onClick: handleLogout,
    },
  ]

  const isProjectsPage = location.pathname === '/projects' || location.pathname.startsWith('/projects/')
  const isHistoryPage = location.pathname === '/history'
  const isEarningsPage = location.pathname === '/earnings'
  const isSettingsPage = location.pathname === '/settings'
  const isAutoLoadPage = location.pathname === '/auto-load'
  const isVDLReportPage = location.pathname === '/reports/vdl-parsing'
  const isSwipePage = location.pathname.includes('/sources/swipe')

  // * Generate breadcrumb items based on current path
  const getBreadcrumbItems = () => {
    const items: Array<{ title: React.ReactNode }> = [
      {
        title: (
          <Space 
            onClick={() => navigate('/projects')}
            style={{ cursor: 'pointer' }}
          >
            <HomeOutlined />
            <span>Главная</span>
          </Space>
        ),
      },
    ]

    if (location.pathname === '/projects') {
      items.push({
        title: (
          <Space>
            <ProjectOutlined />
            <span>Проекты</span>
          </Space>
        ),
      })
    } else if (location.pathname === '/history') {
      items.push({
        title: (
          <Space>
            <HistoryOutlined />
            <span>История</span>
          </Space>
        ),
      })
    } else if (location.pathname === '/earnings') {
      items.push({
        title: (
          <Space>
            <DollarOutlined />
            <span>Заработок</span>
          </Space>
        ),
      })
    } else if (location.pathname === '/settings') {
      items.push({
        title: (
          <Space>
            <SettingOutlined />
            <span>Настройки парсеров</span>
          </Space>
        ),
      })
    } else if (location.pathname === '/auto-load') {
      items.push({
        title: (
          <Space>
            <ApiOutlined />
            <span>API Auto Load</span>
          </Space>
        ),
      })
    } else if (location.pathname === '/reports/vdl-parsing') {
      items.push({
        title: (
          <Space>
            <BarChartOutlined />
            <span>VDL Parsing Report</span>
          </Space>
        ),
      })
    } else if (location.pathname.startsWith('/projects/') && location.pathname.includes('/sources')) {
      items.push(
        {
          title: (
            <Space 
              onClick={() => navigate('/projects')}
              style={{ cursor: 'pointer' }}
            >
              <ProjectOutlined />
              <span>Проекты</span>
            </Space>
          ),
        },
        {
          title: (
            <Space>
              <DatabaseOutlined />
              <span>Источники</span>
            </Space>
          ),
        }
      )
    }

    return items
  }

  return (
    <Layout style={{ minHeight: '100vh', height: isSwipePage ? '100vh' : 'auto', overflow: isSwipePage ? 'hidden' : 'auto' }}>
      {!embedded && (
      <Header style={{
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        background: '#fff',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
        padding: '0 24px',
        height: '64px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '32px', flex: 1, minWidth: 0 }}>
          <div style={{ 
            fontSize: '22px', 
            fontWeight: 'bold', 
            color: '#1890ff',
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
          onClick={() => navigate('/projects')}>
            <HomeOutlined style={{ fontSize: '20px' }} />
            <span>AFI</span>
          </div>
          
          <Space size="middle" style={{ flex: 1, minWidth: 0, justifyContent: 'flex-start' }}>
            <Button
              type={isProjectsPage ? 'primary' : 'default'}
              icon={<ProjectOutlined />}
              onClick={() => navigate('/projects')}
              style={{
                fontSize: '15px',
                height: '36px',
                fontWeight: isProjectsPage ? 500 : 400,
              }}
            >
              Проекты
            </Button>
            
            <Button
              type={isHistoryPage ? 'primary' : 'default'}
              icon={<HistoryOutlined />}
              onClick={() => navigate('/history')}
              style={{
                fontSize: '15px',
                height: '36px',
                fontWeight: isHistoryPage ? 500 : 400,
              }}
            >
              История
            </Button>

            <Button
              type={isEarningsPage ? 'primary' : 'default'}
              icon={<DollarOutlined />}
              onClick={() => navigate('/earnings')}
              style={{
                fontSize: '15px',
                height: '36px',
                fontWeight: isEarningsPage ? 500 : 400,
              }}
            >
              Заработок
            </Button>

            <Button
              type={isVDLReportPage ? 'primary' : 'default'}
              icon={<BarChartOutlined />}
              onClick={() => navigate('/reports/vdl-parsing')}
              style={{
                fontSize: '15px',
                height: '36px',
                fontWeight: isVDLReportPage ? 500 : 400,
              }}
            >
              VDL Parsing
            </Button>
          </Space>
        </div>

        <Space size="middle">
          {/* * Analyzer status indicator */}
          <AnalyzerStatus />

          {/* * Auto Load button */}
          <Button
            type={isAutoLoadPage ? 'primary' : 'default'}
            icon={<ApiOutlined />}
            onClick={() => navigate('/auto-load')}
            style={{
              fontSize: '15px',
              height: '36px',
              fontWeight: isAutoLoadPage ? 500 : 400,
            }}
            title="История API Auto Load"
          >
            API
          </Button>

          {/* * Settings button */}
          <Button
            type={isSettingsPage ? 'primary' : 'default'}
            icon={<SettingOutlined />}
            onClick={() => navigate('/settings')}
            style={{
              fontSize: '15px',
              height: '36px',
              fontWeight: isSettingsPage ? 500 : 400,
            }}
            title="Настройки парсеров"
          >
            Настройки
          </Button>

          <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
            <Space 
              style={{ 
                cursor: 'pointer',
                padding: '4px 8px',
                borderRadius: '4px',
                transition: 'background-color 0.2s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f5f5f5'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent'
              }}
            >
              <Avatar icon={<UserOutlined />} size="small" />
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '0px' }}>
                <Text strong style={{ fontSize: '14px', lineHeight: '1.2' }}>
                  {operator?.username}
                </Text>
                {operator?.role && (
                  <Text type="secondary" style={{ fontSize: '12px', lineHeight: '1.2' }}>
                    {getRoleLabel(operator.role)}
                  </Text>
                )}
              </div>
            </Space>
          </Dropdown>
        </Space>
      </Header>
      )}

      <Content style={{ padding: isSwipePage ? 0 : '24px', background: isSwipePage ? 'transparent' : '#f0f2f5', height: isSwipePage ? (embedded ? '100vh' : 'calc(100vh - 64px)') : 'auto', overflow: isSwipePage ? 'hidden' : 'auto' }}>
        {!isSwipePage && (
          <Breadcrumb
            items={getBreadcrumbItems()}
            style={{ marginBottom: '16px' }}
          />
        )}
        {isSwipePage ? (
          <Outlet />
        ) : (
          <div style={{ 
            background: '#fff', 
            padding: '24px', 
            borderRadius: '8px',
            minHeight: 'calc(100vh - 180px)',
          }}>
            <Outlet />
          </div>
        )}
      </Content>

      {!isSwipePage && (
        <Footer style={{ textAlign: 'center', background: '#f0f2f5' }}>
          AFI ©{new Date().getFullYear()} - Victory CRM Internal Tool
        </Footer>
      )}
    </Layout>
  )
}

export default MainLayout

